/**
 * @file useNegativeImpacts Hook
 * @description React Query hooks for negative impact (risk) operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  negativeImpactsService,
  CreateNegativeImpactDto,
  UpdateNegativeImpactDto,
  BatchUpdateNegativeImpactsDto,
  BatchUpdateResponse,
  NegativeImpactsSummaryResponse,
} from '../services/negative-impacts.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { NegativeImpact, NegativeImpactWithCategory } from '@medsuite/models';

export const negativeImpactKeys = {
  all: ['negative-impacts'] as const,
  lists: () => [...negativeImpactKeys.all, 'list'] as const,
  list: (investmentId: number) => [...negativeImpactKeys.lists(), investmentId] as const,
  summaries: () => [...negativeImpactKeys.all, 'summary'] as const,
  summary: (investmentId: number) => [...negativeImpactKeys.summaries(), investmentId] as const,
};

/**
 * Fetch all negative impacts for an investment
 */
export function useNegativeImpacts(
  investmentId: number,
  options?: Omit<UseQueryOptions<NegativeImpactWithCategory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<NegativeImpactWithCategory[], Error>({
    queryKey: negativeImpactKeys.list(investmentId),
    queryFn: async () => {
      const response = await negativeImpactsService.getAll(investmentId);
      const responseData = unwrapResponse<{ negative_impacts?: NegativeImpactWithCategory[] }>(
        response.data
      );
      return responseData.negative_impacts || [];
    },
    ...options,
  });
}

/**
 * Fetch negative impacts summary for an investment
 */
export function useNegativeImpactsSummary(
  investmentId: number,
  options?: Omit<
    UseQueryOptions<NegativeImpactsSummaryResponse['summary'], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<NegativeImpactsSummaryResponse['summary'], Error>({
    queryKey: negativeImpactKeys.summary(investmentId),
    queryFn: async () => {
      const response = await negativeImpactsService.getSummary(investmentId);
      const responseData = unwrapResponse<NegativeImpactsSummaryResponse>(response.data);
      return responseData.summary;
    },
    ...options,
  });
}

/**
 * Create new negative impact
 */
export function useCreateNegativeImpact(
  investmentId: number,
  options?: UseMutationOptions<NegativeImpact, Error, CreateNegativeImpactDto>
) {
  const queryClient = useQueryClient();

  return useMutation<NegativeImpact, Error, CreateNegativeImpactDto>({
    mutationFn: async (data) => {
      const response = await negativeImpactsService.create(investmentId, data);
      return unwrapResponse<NegativeImpact>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Update negative impact
 */
export function useUpdateNegativeImpact(
  investmentId: number,
  options?: UseMutationOptions<
    NegativeImpact,
    Error,
    { negativeImpactId: number; data: UpdateNegativeImpactDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    NegativeImpact,
    Error,
    { negativeImpactId: number; data: UpdateNegativeImpactDto }
  >({
    mutationFn: async ({ negativeImpactId, data }) => {
      const response = await negativeImpactsService.update(investmentId, negativeImpactId, data);
      return unwrapResponse<NegativeImpact>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete negative impact
 */
export function useDeleteNegativeImpact(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (negativeImpactId) => {
      await negativeImpactsService.delete(investmentId, negativeImpactId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Batch update negative impacts (create, update, delete in one request)
 */
export function useBatchUpdateNegativeImpacts(
  investmentId: number,
  options?: UseMutationOptions<BatchUpdateResponse, Error, BatchUpdateNegativeImpactsDto>
) {
  const queryClient = useQueryClient();

  return useMutation<BatchUpdateResponse, Error, BatchUpdateNegativeImpactsDto>({
    mutationFn: async (data) => {
      const response = await negativeImpactsService.batchUpdate(investmentId, data);
      return unwrapResponse<BatchUpdateResponse>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: negativeImpactKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}
